import React, { FC, ReactNode, useMemo } from 'react';
import { ConnectionProvider, WalletProvider } from '@solana/wallet-adapter-react';
import { WalletAdapterNetwork } from '@solana/wallet-adapter-base';
import {
  SolflareWalletAdapter,
  TorusWalletAdapter,
  LedgerWalletAdapter, // Assurez-vous d'importer JupiterWalletAdapter si nécessaire
} from '@solana/wallet-adapter-wallets';
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui';
import { clusterApiUrl } from '@solana/web3.js';

// Import des styles CSS par défaut pour les composants de wallet
import '@solana/wallet-adapter-react-ui/styles.css';

interface WalletContextProviderProps {
  children: ReactNode;
}

export const WalletContextProvider: FC<WalletContextProviderProps> = ({ children }) => {
  // Configuration du réseau (mainnet-beta, testnet, ou devnet)
  const network = WalletAdapterNetwork.Mainnet;

  // URL de l'endpoint RPC
  const endpoint = useMemo(() => clusterApiUrl(network), [network]);

  // Configuration des wallets supportés avec gestion optimisée des conflits
  const wallets = useMemo(
    () => {
      // Fonction pour supprimer les conflits entre extensions
      const cleanupWalletConflicts = () => {
        // Nettoyer les conflits Solflare/Metamask
        if (typeof window !== 'undefined') {
          // Éviter les conflits entre Solflare et Metamask
          try {
            // Priorité à Solflare pour Solana
            const anyWindow = window as any;
            if (anyWindow.solflare && anyWindow.ethereum) {
              console.log('🔧 Résolution conflit Solflare/Metamask - Priorité à Solflare');
            }
          } catch (error) {
            console.warn('Erreur résolution conflit wallet:', error);
          }
        }
      };

      cleanupWalletConflicts();

      return [
        // Solflare en premier pour la priorité
        new SolflareWalletAdapter(),
        new TorusWalletAdapter(),
        new LedgerWalletAdapter(),
        // Phantom sera détecté automatiquement via le Wallet Standard
      ];
    },
    [network]
  );

  // Configuration du provider avec gestion d'erreur améliorée
  const walletConfig = useMemo(() => ({
    wallets,
    autoConnect: true,
    onError: (error: any) => {
      // Ignorer les erreurs de détection de wallet non critiques
      if (error?.message?.includes('solflare-detect-metamask') || 
          error?.message?.includes('StreamMiddleware')) {
        console.log('🔧 Conflit wallet détecté et ignoré:', error.message);
        return; // Ignore these specific errors
      }
      
      // Log autres erreurs pour debugging
      console.error('Erreur wallet adapter:', error);
    }
  }), [wallets]);

  return (
    <ConnectionProvider endpoint={endpoint}>
      <WalletProvider 
        wallets={walletConfig.wallets} 
        autoConnect={walletConfig.autoConnect}
        onError={walletConfig.onError}
      >
        <WalletModalProvider>
          {children}
        </WalletModalProvider>
      </WalletProvider>
    </ConnectionProvider>
  );
};
